// src/components/admin/OrgSettingsSection.tsx
// Allows org admins to set branding: brand_name, app_display_name, logo_url.
import { useAuth } from "@/contexts/AuthContext";
import { requireSupabase } from "@/lib/supabase";
import { OrgSettingsSectionBrandingGrid } from "@/components/admin/OrgSettingsSectionBrandingGrid";
import { OrgSettingsSectionFulfillment } from "@/components/admin/OrgSettingsSectionFulfillment";
import { OrgSettingsSectionService } from "@/components/admin/OrgSettingsSectionService";
import { OrgSettingsSectionPersonalAssistant } from "@/components/admin/OrgSettingsSectionPersonalAssistant";
import { OrgSettingsSectionKbPilot } from "@/components/admin/OrgSettingsSectionKbPilot";
import { OrgSettingsSectionOpportunityDefaults } from "@/components/admin/OrgSettingsSectionOpportunityDefaults";
import { OrgSettingsSectionProposalContact } from "@/components/admin/OrgSettingsSectionProposalContact";
import { OrgSettingsSectionPricingCatalog } from "@/components/admin/OrgSettingsSectionPricingCatalog";
import { useOrgSettingsSection } from "@/components/admin/useOrgSettingsSection";
import { useUnsavedChangesPrompt } from "@/hooks/useUnsavedChangesPrompt";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

/** Functional groups for Org Administration accordions (dev-sync 32a24eaa). */
export type OrgSettingsGroup = "branding" | "sales" | "product_suites" | "all";

export function OrgSettingsSection({
  organizationId,
  plain,
  group = "all",
}: {
  organizationId: string;
  plain?: boolean;
  group?: OrgSettingsGroup;
}) {
  const auth = useAuth();
  const showPricingCatalog = auth.status === "ready" && auth.hasRole("platform_owner", "platform_dev");
  const sb = requireSupabase();
  const {
    settings,
    setSettings,
    original,
    loading,
    syncing,
    msg,
    setMsg,
    confirmPending,
    setConfirmPending,
    kbTestingModeEnabled,
    setKbTestingModeEnabled,
    hasSalesFloor,
    setHasSalesFloor,
    savingHasSalesFloor,
    servicesEnabled,
    setServicesEnabled,
    savingServicesEnabled,
    personalAssistantEnabled,
    setPersonalAssistantEnabled,
    savingPersonalAssistantEnabled,
    hasBrandingChange,
    isDirty,
    syncFromGhl,
    save,
    saveKbTestingMode,
    saveHasSalesFloor,
    saveServicesEnabled,
    savePersonalAssistantEnabled,
  } = useOrgSettingsSection(sb, organizationId);

  const showBranding = group === "all" || group === "branding";
  const showSales = group === "all" || group === "sales";
  const showSuites = group === "all" || group === "product_suites";

  // Participate in the workspace-level unsaved-changes guard so that a
  // bucket/mode click while the branding form has pending edits surfaces
  // the Save / Discard / Cancel modal instead of silently dropping the
  // edits. The sibling toggles (KB testing mode, Sales floor) save on
  // click, so they aren't included in the dirty registry.
  useUnsavedChangesPrompt({
    id: `org-settings:branding:${group}`,
    label: "Org Settings",
    dirty: showBranding && isDirty,
    save,
  });

  return (
    <section className={plain ? "space-y-4" : "p-4 rounded-xl bg-white border border-gray-200 space-y-4"}>
      {!plain && group === "all" && (
        <div>
          <h2 className="text-lg font-semibold">Org Settings</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Branding shown in the app header and reports. Requires re-running{" "}
            <span className="font-mono">supabase_admin_onboarding.sql</span> to activate.
          </p>
        </div>
      )}

      {showBranding && msg && (
        <div
          className={`text-sm rounded-lg px-3 py-2 border ${
            msg.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          {msg.text}
        </div>
      )}

      {showBranding ? (
        <OrgSettingsSectionBrandingGrid
          settings={settings}
          loading={loading}
          syncing={syncing}
          setSettings={setSettings}
          onSyncFromGhl={syncFromGhl}
        />
      ) : null}

      {showBranding ? (
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          Product lines are authored in Ops → Deal Types. This page no longer stores product types.
        </p>
      ) : null}

      {showSales ? (
        <>
          <OrgSettingsSectionOpportunityDefaults sb={sb} organizationId={organizationId} />
          <OrgSettingsSectionProposalContact sb={sb} organizationId={organizationId} branding={settings} />
          {showPricingCatalog ? <OrgSettingsSectionPricingCatalog organizationId={organizationId} /> : null}
        </>
      ) : null}

      {showSuites ? (
        <>
          <OrgSettingsSectionKbPilot
            kbTestingModeEnabled={kbTestingModeEnabled}
            setKbTestingModeEnabled={setKbTestingModeEnabled}
            onSaveKbTestingMode={saveKbTestingMode}
          />
          <OrgSettingsSectionFulfillment
            hasSalesFloor={hasSalesFloor}
            setHasSalesFloor={setHasSalesFloor}
            saving={savingHasSalesFloor}
            onSave={saveHasSalesFloor}
          />
          <OrgSettingsSectionService
            servicesEnabled={servicesEnabled}
            setServicesEnabled={setServicesEnabled}
            saving={savingServicesEnabled}
            onSave={saveServicesEnabled}
          />
          <OrgSettingsSectionPersonalAssistant
            personalAssistantEnabled={personalAssistantEnabled}
            setPersonalAssistantEnabled={setPersonalAssistantEnabled}
            saving={savingPersonalAssistantEnabled}
            onSave={savePersonalAssistantEnabled}
          />
        </>
      ) : null}

      {showBranding && settings.logo_url ? (
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">Preview:</span>
          <img
            src={settings.logo_url}
            alt="Logo preview"
            className="h-10 object-contain rounded border border-gray-200"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
      ) : null}

      {showBranding ? (
        confirmPending ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 space-y-2">
            <p className="text-sm font-semibold text-amber-900">Confirm branding change</p>
            <p className="text-xs text-amber-800">
              You are changing visible branding (app name or colors). This will update the appearance for all users in
              this organization immediately after saving.
            </p>
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setConfirmPending(false);
                  void save();
                }}
                disabled={loading}
                className="px-3 py-2 rounded-lg bg-amber-600 text-white text-sm hover:bg-amber-700 disabled:opacity-50"
              >
                Confirm save
              </button>
              <button
                type="button"
                onClick={() => setConfirmPending(false)}
                className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "admin-org-profile-save" }}
              onClick={() => {
                if (hasBrandingChange) {
                  setConfirmPending(true);
                } else {
                  void save();
                }
              }}
              disabled={loading || !isDirty}
              className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              Save settings
            </button>
            {isDirty && (
              <button
                type="button"
                onClick={() => {
                  setSettings(original);
                  setMsg(null);
                }}
                className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
              >
                Discard
              </button>
            )}
          </div>
        )
      ) : null}
    </section>
  );
}
